// Time utility functions for formatting relative and absolute time

/**
 * Converts a timestamp to relative time format
 * - Less than 1 minute: "now" 
 * - 1-59 seconds: "Xs"
 * - 1-59 minutes: "Xm"
 * - 1-23 hours: "Xhr ago"
 * - 1-6 days: "Xd ago"
 * - 7+ days: "M/D" format (e.g., "10/5")
 * - 1+ year: "Xy ago"
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} - Formatted time string
 */
export const timeAgo = (timestamp) => {
    if (!timestamp) return '';

    const now = new Date();
    const past = new Date(timestamp);

    // Handle invalid dates
    if (isNaN(past.getTime())) return '';

    const diffInMs = now - past;
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInYears = Math.floor(diffInDays / 365);

    // Less than 1 minute
    if (diffInSeconds < 60) {
        if (diffInSeconds < 5) return 'now';
        return `${diffInSeconds}s`;
    }

    // Minutes (1-59)
    if (diffInMinutes < 60) {
        return `${diffInMinutes}m`;
    }

    // Hours (1-23)
    if (diffInHours < 24) {
        return `${diffInHours}hr ago`;
    }

    // Days (1-6)
    if (diffInDays <= 6) {
        return `${diffInDays}d ago`;
    }

    // 1 year or more
    if (diffInYears >= 1) {
        return `${diffInYears}y ago`;
    }

    // 7+ days but less than 1 year - show M/D format
    const month = past.getMonth() + 1; // getMonth() returns 0-11, so add 1
    const day = past.getDate();

    // If it's the same year, just show M/D
    if (past.getFullYear() === now.getFullYear()) {
        return `${month}/${day}`;
    }

    // If it's a different year, show M/D/YY
    const year = past.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
};

/**
 * Alternative format for short relative time
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} - Formatted short time string (e.g., "2m", "1hr", "3d")
 */
export const timeAgoShort = (timestamp) => {
    if (!timestamp) return '';

    const now = new Date();
    const past = new Date(timestamp);

    if (isNaN(past.getTime())) return '';

    const diffInMs = now - past;
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInSeconds < 5) return 'now';
    if (diffInSeconds < 60) return `${diffInSeconds}s`;
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInHours < 24) return `${diffInHours}hr`;
    if (diffInDays <= 6) return `${diffInDays}d`;
    if (diffInYears >= 1) return `${diffInYears}y`;

    // For 7+ days, return M/D format
    const month = past.getMonth() + 1;
    const day = past.getDate();

    return `${month}/${day}`;
};